// 📁 src/pages/TarotCardPage.js
import React from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";

/* =========================
   Background & Theme (more turquoise)
========================= */
const CARD_BG = `
 radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;
/* Distinct frosted cards */
const BOX_BASE =
  "rounded-xl p-5 border backdrop-blur-md shadow-[0_6px_20px_rgba(0,0,0,0.35)] relative overflow-hidden";

const UPRIGHT_BG = "bg-gradient-to-b from-[#03779B]/70 via-[#0C4B84]/65 to-[#005C83]/20";
const REVERSED_BG = "bg-gradient-to-b from-[#821E3C]/60 via-[#014C75]/55 to-[#021868]/25";

/* Site-wide SEO constants (match About.js) */
const siteUrl = "https://tarotfate.com";
const ogImage = `${siteUrl}/icons/og-cover.jpg`;   // global OG cover (not logo)
const logoWebp = `${siteUrl}/icons/logo.webp`;     // organization logo

export default function TarotCardPage() {
  const { cardSlug } = useParams();
  const card = cardData[cardSlug];

  const suitToPath = {
    "Major Arcana": "/tarot/major-arcana-tarot-suit-cards-meaning",
    Wands: "/tarot/wands-tarot-suit-cards-meaning",
    Cups: "/tarot/cups-tarot-suit-cards-meaning",
    Swords: "/tarot/swords-tarot-suit-cards-meaning",
    Pentacles: "/tarot/pentacles-tarot-suit-cards-meaning",
  };

  if (!card) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: CARD_BG }}>
        <div className="text-center p-10 rounded-2xl bg-white/10 backdrop-blur-lg border border-white/15 text-white">
          <h2 className="text-xl font-semibold">Tarot card not found</h2>
          <p className="text-sm mt-2 text-white/80">Check the URL or browse all cards.</p>
          <Link to="/tarot-cards" className="underline">All Tarot Cards</Link>
        </div>
      </div>
    );
  }

  /* =========================
     SEO helpers
  ========================= */
  const pageUrl = `${siteUrl}/tarot-card/${card.slug}`;
  const cardImg = card.image || `${siteUrl}/card/${card.slug}.webp`;

  const splitFirstMeaning = (arr = []) => {
    if (!arr?.length) return ["Meaning", ""];
    const [label, ...rest] = String(arr[0]).split(" – ");
    return [label || "Meaning", rest.join(" – ") || ""];
  };
  const [uprightLabel0, uprightDesc0] = splitFirstMeaning(card.upright);
  const [reversedLabel0, reversedDesc0] = splitFirstMeaning(card.reversed);

  const pickAspect = (items = [], key = "") => {
    const lower = key.toLowerCase();
    const raw =
      items?.find((x) => x.toLowerCase().startsWith(`${lower} –`)) ||
      items?.find((x) => x.toLowerCase().includes(`${lower} –`)) ||
      null;
    if (!raw) return null;
    const [_l, ...rest] = raw.split(" – ");
    return rest.join(" – ").trim();
  };

  const uprightLove = pickAspect(card.upright, "Love");
  const uprightCareer = pickAspect(card.upright, "Career");
  const reversedLove = pickAspect(card.reversed, "Love");
  const reversedCareer = pickAspect(card.reversed, "Career");

  const metaDescription = [
    card.subtitle,
    uprightDesc0 && `Upright: ${uprightDesc0}`,
    reversedDesc0 && `Reversed: ${reversedDesc0}`,
  ]
    .filter(Boolean)
    .join(" ");

  // Keyword set (mega + card-specific)
  const megaKeywords = `
tarot, tarot reading, tarot card meanings, free tarot, online tarot reading, accurate tarot, instant tarot, yes or no tarot,
one card reading, three card spread, five card spread, Celtic Cross spread, past present future tarot, upright meanings,
reversed meanings, major arcana meanings, minor arcana meanings, wands suit meaning, cups suit meaning, swords suit meaning,
pentacles suit meaning, how to read tarot, beginner tarot guide, advanced tarot guide, tarot tips and spreads,
tarot in English, tarot en Español, tarot em Português, tarot en Français, tarot auf Deutsch, tarocchi in Italiano, tarot Türkçe,
تاروت عربي, टैरो हिंदी, ٹروٹ اردو, tarot বাংলা, tarot 日本語, tarot 한국어, tarot Bahasa Indonesia, tarot Tagalog,
tarot Tiếng Việt, tarot ไทย, USA tarot reading, UK tarot reading, Canada tarot reading, Australia tarot reading, India tarot reading,
Pakistan tarot reading, Bangladesh tarot reading, Sri Lanka tarot reading, Nepal tarot reading, Philippines tarot reading,
Malaysia tarot reading, Singapore tarot reading, Indonesia tarot reading, Thailand tarot reading, Vietnam tarot reading,
Japan tarot reading, South Korea tarot reading, China tarot reading, Hong Kong tarot reading, Taiwan tarot reading,
UAE tarot reading, Saudi Arabia tarot reading, Qatar tarot reading, Kuwait tarot reading, Oman tarot reading, Egypt tarot reading,
Morocco tarot reading, South Africa tarot reading, Kenya tarot reading, Nigeria tarot reading, Ghana tarot reading, Ethiopia tarot reading,
Turkey tarot reading, Iran tarot reading, Israel tarot reading, Germany tarot reading, France tarot reading, Spain tarot reading,
Portugal tarot reading, Italy tarot reading, Netherlands tarot reading, Belgium tarot reading, Switzerland tarot reading, Austria tarot reading,
Sweden tarot reading, Norway tarot reading, Denmark tarot reading, Finland tarot reading, Poland tarot reading, Czech Republic tarot reading,
Slovakia tarot reading, Hungary tarot reading, Romania tarot reading, Bulgaria tarot reading, Greece tarot reading, Croatia tarot reading,
Serbia tarot reading, Mexico tarot reading, Brazil tarot reading, Argentina tarot reading, Chile tarot reading, Colombia tarot reading,
Peru tarot reading, tarot near me, online psychic tarot, oracle card reading, angel card reading, moon reading, zodiac tarot,
Aries tarot, Taurus tarot, Gemini tarot, Cancer tarot, Leo tarot, Virgo tarot, Libra tarot, Scorpio tarot, Sagittarius tarot,
Capricorn tarot, Aquarius tarot, Pisces tarot, tarot today, tarot tonight, no signup tarot, mobile tarot, fast tarot results,
${card.name}, ${card.slug}, ${card.suit || ""} tarot, ${card.emotions ? card.emotions.join(", ") : ""}, meaning of ${card.name},
${card.name} upright, ${card.name} reversed, ${card.name} love meaning, ${card.name} career meaning, ${card.name} advice
`.replace(/\s+/g, " ").trim();

  // JSON-LD graph (About.js style: logo.webp + og-cover.jpg)
  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: logoWebp,
          width: 512,
          height: 512
        }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en"
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${card.name} — Meaning, Upright & Reversed | TarotFate`,
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        inLanguage: "en",
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` }
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover"
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "Tarot Cards", item: `${siteUrl}/tarot-cards` },
          ...(card.suit
            ? [
                {
                  "@type": "ListItem",
                  position: 3,
                  name: card.suit,
                  item: `${siteUrl}${suitToPath[card.suit]}`
                }
              ]
            : []),
          { "@type": "ListItem", position: card.suit ? 4 : 3, name: card.name, item: pageUrl }
        ]
      },
      {
        "@type": "CreativeWork",
        "@id": `${pageUrl}#card`,
        name: card.name,
        headline: `${card.name} Meaning — Upright & Reversed`,
        description: metaDescription,
        url: pageUrl,
        image: cardImg,
        about: [card.suit ? { "@type": "Thing", name: card.suit } : undefined].filter(Boolean),
        keywords: megaKeywords,
        isPartOf: { "@id": `${pageUrl}#webpage` },
        inLanguage: "en"
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: [
          {
            "@type": "Question",
            name: `What does ${card.name} mean upright?`,
            acceptedAnswer: {
              "@type": "Answer",
              text: uprightDesc0 || `Discover the upright qualities and guidance of ${card.name}.`
            }
          },
          {
            "@type": "Question",
            name: `What does ${card.name} mean reversed?`,
            acceptedAnswer: {
              "@type": "Answer",
              text: reversedDesc0 || `Explore the reversed themes and lessons of ${card.name}.`
            }
          },
          ...(uprightLove
            ? [
                {
                  "@type": "Question",
                  name: `${card.name} in love (upright)`,
                  acceptedAnswer: { "@type": "Answer", text: uprightLove }
                }
              ]
            : []),
          ...(reversedLove
            ? [
                {
                  "@type": "Question",
                  name: `${card.name} in love (reversed)`,
                  acceptedAnswer: { "@type": "Answer", text: reversedLove }
                }
              ]
            : []),
          ...(uprightCareer
            ? [
                {
                  "@type": "Question",
                  name: `${card.name} in career (upright)`,
                  acceptedAnswer: { "@type": "Answer", text: uprightCareer }
                }
              ]
            : []),
          ...(reversedCareer
            ? [
                {
                  "@type": "Question",
                  name: `${card.name} in career (reversed)`,
                  acceptedAnswer: { "@type": "Answer", text: reversedCareer }
                }
              ]
            : [])
        ]
      },
      {
        "@type": "HowTo",
        "@id": `${pageUrl}#howto`,
        name: `How to read ${card.name} in a tarot spread`,
        description: `Practical steps for interpreting ${card.name} using upright and reversed meanings.`,
        step: [
          { "@type": "HowToStep", name: "Observe the imagery", text: "Notice symbols, posture, and setting that hint at the card’s tone." },
          { "@type": "HowToStep", name: "Place in context", text: "Consider the suit, number, and spread position to frame the message." },
          { "@type": "HowToStep", name: "Check upright vs reversed", text: "Read both lists and choose what best fits the question." },
          { "@type": "HowToStep", name: "Relate to the question", text: "Tie keywords back to love, career, or personal growth as needed." },
          { "@type": "HowToStep", name: "Synthesize", text: "Summarize the takeaway in one sentence and note any action step." }
        ],
        inLanguage: "en"
      }
    ]
  };

  return (
    <div className="min-h-screen" style={{ background: CARD_BG }}>
      {/* Centralized SEO via reusable component (About.js pattern) */}
      <Seo
        title={`${card.name} — Meaning, Upright & Reversed | TarotFate`}
        description={metaDescription}
        url={pageUrl}
        image={ogImage}        // consistent OG image
        schema={schemaGraph}
        keywords={megaKeywords}
        includeMega={true}
      />

      <div className="px-4 sm:px-8 md:px-16 py-6 max-w-6xl mx-auto text-white/95 font-[Cormorant_Garamond]">
        {/* Breadcrumb */}
        <nav className="text-sm sm:text-base text-white/85 mb-2 space-x-2">
          <Link to="/tarot-cards" className="hover:underline">All Tarot Cards</Link>
          {card.suit && (
            <>
              <span>›</span>
              <Link to={suitToPath[card.suit]} className="hover:underline">
                {card.suit} Suit
              </Link>
            </>
          )}
        </nav>

        <div className="h-px bg-white/25 mt-2 mb-6" />

        {/* Layout */}
        <div className="flex flex-col md:flex-row gap-4 md:items-start">
          {/* Mobile Title */}
          <div className="md:hidden order-1 text-center">
            <h1 className="text-2xl font-semibold mb-1 text-white">{card.name}</h1>
            <h2 className="text-lg text-white/80 italic mb-0">{card.subtitle}</h2>
          </div>

          {/* Image */}
          <div className="md:w-1/2 w-full flex justify-center md:justify-end items-start md:pt-1 pt-2 order-2 md:order-10">
            <img
              src={cardImg}
              alt={card.name}
              className="w-[200px] md:w-[460px] lg:w-[580px] h-auto select-none pointer-events-none rounded-lg shadow-[0_16px_48px_rgba(0,0,0,0.45)]"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px bg-white/25" />

          {/* Desktop Title + Description */}
          <div className="md:w-2/3 order-3">
            <div className="hidden md:block">
              <h1 className="text-3xl font-semibold mb-2 text-white">{card.name}</h1>
              <h2 className="text-lg text-white/80 italic mb-4">{card.subtitle}</h2>
            </div>
            <p className="text-[17px] leading-relaxed whitespace-pre-line mb-1 text-white/90">
              {card.description}
            </p>
          </div>
        </div>

        <div className="h-px bg-white/25 my-10" />

        {/* Upright vs Reversed (distinct looks) */}
        <div className="mt-5">
          <h2 className="text-xl font-semibold mb-4 text-white text-center">
            Upright vs Reversed Meaning
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* UPRIGHT — brighter turquoise glass */}
            <div className={`${BOX_BASE} ${UPRIGHT_BG} border-white/40`}>
              {/* subtle ornament */}
              <div
                className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-65"
                style={{
                  background:
                    "radial-gradient(circle, rgba(3, 46, 155, 0.55), rgba(3,121,155,0) 62%)",
                }}
              />
              <div className="flex justify-center">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-none bg-white/15 border border-white/40 text-white/95 text-sm">
                  ↑  Upright Interpretation
                </span>
              </div>
              <ul className="mt-4 space-y-2 text-[16px] leading-relaxed">
                {card.upright.map((item, index) => {
                  const [label, ...descParts] = item.split(" – ");
                  return (
                    <li key={index} className="text-white/95">
                      <span className="font-semibold text-white">{label}:</span>{" "}
                      {descParts.join(" – ")}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* REVERSED — plum-tinted glass with deep navy fade */}
            <div className={`${BOX_BASE} ${REVERSED_BG} border-white/40 [box-shadow:0_6px_24px_rgba(130,30,60,0.35)]`}>
              {/* subtle ornament */}
              <div
                className="pointer-events-none absolute -bottom-12 -left-10 w-56 h-56 rounded-full opacity-65"
                style={{
                  background:
                    "radial-gradient(circle, rgba(130,30,60,0.55), rgba(130,30,60,0) 62%)",
                }}
              />
              <div className="flex justify-center">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-none bg-white/10 border border-white/35 text-white/95 text-sm">
                  ↯  Reversed Interpretation
                </span>
              </div>
              <ul className="mt-4 space-y-2 text-[16px] leading-relaxed">
                {card.reversed.map((item, index) => {
                  const [label, ...descParts] = item.split(" – ");
                  return (
                    <li key={index} className="text-white/90">
                      <span className="font-semibold text-white">{label}:</span>{" "}
                      {descParts.join(" – ")}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        <div className="h-px bg-white/25 my-10" />

        {/* How to Read */}
        <h3 className="text-xl font-semibold mb-3 text-white">How to Read This Card</h3>
        <p className="text-[16.5px] whitespace-pre-line mb-8 text-white/90">
          {card.reading}
        </p>

        <div className="my-6 h-[2px] bg-gradient-to-r from-transparent via-white/60 to-transparent" />

        {/* Mirroring Cards */}
        {card.mirroring?.length > 0 && (
          <section className="mt-3">
            <h3 className="text-xl text-center font-semibold mb-2 text-white">Mirroring Cards</h3>
            <p className="text-base text-center text-white/85 mb-4">
              These cards resonate with or contrast this card’s themes. Explore them for deeper context:
            </p>

            <div className="mt-2 flex flex-wrap justify-center gap-6">
              {card.mirroring.map((mirrorSlug) => {
                const mirroredCard = Object.values(cardData).find((c) => c.slug === mirrorSlug);
                if (!mirroredCard) return null;
                return (
                  <Link to={`/tarot-card/${mirroredCard.slug}`} key={mirrorSlug} className="w-[120px] group">
                    <div className="rounded-xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-md shadow-[0_6px_20px_rgba(0,0,0,0.35)] group-hover:shadow-[0_2px_8px_rgba(255,255,255,0.8)] transition">
                      <img src={mirroredCard.image} alt={mirroredCard.name} className="w-full h-auto" />
                    </div>
                    <p className="mt-2 text-center text-sm font-medium text-white/90 group-hover:text-white">
                      {mirroredCard.name}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Emotional Themes */}
        {card.emotions?.length > 0 && (
          <>
            <div className="my-8 h-[2px] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
            <div className="mt-2">
              <h3 className="text-xl font-semibold mb-4 text-white">Emotional Themes</h3>
              <p className="text-sm text-white/85 mb-3">This card may appear when you are feeling:</p>
              <div className="flex gap-3 flex-wrap">
                {card.emotions.map((emotion) => (
                  <Link
                    key={emotion}
                    to={`/emotions/${emotion.replace(/\s+/g, "-").toLowerCase()}`}
                    className="border border-white/45 bg-white/10 text-white/95 text-sm px-3 py-1 rounded-full hover:bg-white/20 transition"
                  >
                    {emotion.replace(/-/g, " ")}
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="my-8 h-[2px] bg-gradient-to-r from-transparent via-white/60 to-transparent" />

        {/* Suit Navigation Buttons */}
        <div className="text-center text-white space-y-4 mb-4">
          <div className="flex flex-wrap justify-center gap-3 mt-1">
            <Link
              to={suitToPath["Major Arcana"]}
              className="px-4 py-1 rounded-full border border-white/55 bg-white/10 text-sm font-medium text-white hover:bg-white/20 transition"
            >
              ✦ Major Arcana
            </Link>
            <Link
              to={suitToPath["Wands"]}
              className="px-4 py-1 rounded-full border border-white/55 bg-white/10 text-sm font-medium text-white hover:bg-white/20 transition"
            >
              ✦ Wands
            </Link>
            <Link
              to={suitToPath["Cups"]}
              className="px-4 py-1 rounded-full border border-white/55 bg-white/10 text-sm font-medium text-white hover:bg-white/20 transition"
            >
              ✦ Cups
            </Link>
            <Link
              to={suitToPath["Swords"]}
              className="px-4 py-1 rounded-full border border-white/55 bg-white/10 text-sm font-medium text-white hover:bg-white/20 transition"
            >
              ✦ Swords
            </Link>
            <Link
              to={suitToPath["Pentacles"]}
              className="px-4 py-1 rounded-full border border-white/55 bg-white/10 text-sm font-medium text-white hover:bg-white/20 transition"
            >
              ✦ Pentacles
            </Link>
            <Link
              to="/tarot-cards"
              className="px-4 py-1 rounded-full border border-white/55 bg-white/10 text-sm font-medium text-white hover:bg-white/20 transition"
            >
              ✦ All Cards
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
